import { useState } from "react";
import { Droppable, Draggable } from "react-beautiful-dnd";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  Delete02Icon,
  DragDropVerticalIcon,
  ViewIcon,
  ViewOffSlashIcon,
  Cancel01Icon,
  Settings02Icon,
} from "@hugeicons/core-free-icons";
import { Content } from "@/types";
import { MenuSection } from "@/types/menus";
import { getThumbnailImage } from "@/utils/media";
import { useAppSelector, useTerminology } from "@/hooks";
import { useUpdateContentMutation } from "@/features/organizations/api/contentApi";
import Price from "@/components/Price";

const DroppableComp = Droppable as any;
const DraggableComp = Draggable as any;

interface Props {
  section: MenuSection;
  sectionIndex: number;
  allContent: Content[];
  expanded: boolean;
  onToggleExpand: () => void;
  onUpdateSection: (patch: Partial<MenuSection>) => void;
  onRemoveSection: () => void;
  onRemoveProduct: (productId: string) => void;
  dragHandleProps?: any;
  draggableProps?: any;
  innerRef?: any;
  isSelected?: boolean;
  onSelectSection?: () => void;
}

const SectionCard = ({
  section,
  sectionIndex,
  allContent,
  expanded,
  onToggleExpand,
  onUpdateSection,
  onRemoveSection,
  onRemoveProduct,
  dragHandleProps,
  draggableProps,
  innerRef,
  isSelected,
  onSelectSection,
}: Props) => {
  const activeOrganization = useAppSelector((s) => s.ui.activeOrganization);
  const activeBranch = useAppSelector((s) => s.ui.activeBranch);
  const { t } = useTerminology();
  const [updateContent] = useUpdateContentMutation();
  const [editingTitle, setEditingTitle] = useState(false);
  const droppableId = section.category;

  const sectionProducts = (section.products ?? [])
    .map((id) => allContent.find((c) => c.id === id))
    .filter(Boolean) as Content[];

  const handleToggleProductVisibility = (product: Content, isCurrentlyDisabled: boolean) => {
    if (!activeOrganization || !product.id) return;
    updateContent({
      organizationId: activeOrganization,
      contentId: product.id,
      content: {
        isDisabled: activeBranch ? Boolean(product.isDisabled) : !isCurrentlyDisabled,
        ...(activeBranch && {
          branchSettings: {
            ...product.branchSettings,
            [activeBranch]: {
              ...product.branchSettings?.[activeBranch],
              isDisabled: !isCurrentlyDisabled,
            },
          },
        }),
      } as Content,
    });
  };

  return (
    <Accordion
      ref={innerRef}
      {...draggableProps}
      expanded={expanded}
      onChange={onToggleExpand}
      disableGutters
      elevation={0}
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        "&::before": { display: "none" },
        border: "2px solid",
        borderColor: isSelected ? "primary.main" : "divider",
        boxSizing: "border-box",
        borderRadius: "12px !important",
        "&:first-of-type": {
          borderTopLeftRadius: "12px !important",
          borderTopRightRadius: "12px !important",
        },
        "&:last-of-type": {
          borderBottomLeftRadius: "12px !important",
          borderBottomRightRadius: "12px !important",
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <HugeiconsIcon icon={ArrowDown01Icon} size={18} strokeWidth={2} />
        }
        sx={{ px: 1.5 }}
      >
        <Stack direction="row" alignItems="center" flex={1} gap={1} minWidth={0}>
          {/* Drag handle for the section */}
          <Box
            {...dragHandleProps}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{ cursor: "grab", display: "flex", alignItems: "center", color: "text.disabled" }}
          >
            <HugeiconsIcon icon={DragDropVerticalIcon} size={20} strokeWidth={2} />
          </Box>

          {/* Title (editable inline) */}
          {editingTitle ? (
            <TextField
              value={section.categoryTitle ?? ""}
              size="small"
              autoFocus
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onUpdateSection({ categoryTitle: e.target.value })}
              onBlur={() => setEditingTitle(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Escape") setEditingTitle(false);
              }}
              sx={{ flex: 1 }}
            />
          ) : (
            <Stack direction="row" alignItems="center" gap={1} flex={1}>
              <Typography
                fontWeight={500}
                noWrap
                sx={{
                  textDecoration: section.isVisible === false ? "line-through" : "none",
                  color: section.isVisible === false ? "text.disabled" : isSelected ? "primary.main" : "text.primary",
                  cursor: "text",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingTitle(true);
                }}
              >
                {section.categoryTitle || "Sin título"}
              </Typography>
              <Chip
                color="primary"
                label={sectionProducts.length}
                size="small"
                sx={{ minWidth: 28 }}
              />
            </Stack>
          )}


          {/* Visibility toggle */}
          <Tooltip title={section.isVisible === false ? "Mostrar sección" : "Ocultar sección"}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onUpdateSection({ isVisible: section.isVisible === false ? true : false });
              }}
            >
              <HugeiconsIcon
                icon={section.isVisible === false ? ViewOffSlashIcon : ViewIcon}
                size={18}
                strokeWidth={2}
              />
            </IconButton>
          </Tooltip>

          {/* Settings / Config */}
          <Tooltip title="Configurar sección">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onSelectSection?.();
              }}
            >
              <HugeiconsIcon icon={Settings02Icon} size={18} strokeWidth={2} />
            </IconButton>
          </Tooltip>

          {/* Delete section */}
          <Tooltip title="Eliminar sección">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveSection();
              }}
            >
              <HugeiconsIcon icon={Delete02Icon} size={18} strokeWidth={2} />
            </IconButton>
          </Tooltip>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
        <DroppableComp droppableId={droppableId} type="PRODUCT">
          {(provided: import("react-beautiful-dnd").DroppableProvided, snapshot: import("react-beautiful-dnd").DroppableStateSnapshot) => (
            <Stack
              ref={provided.innerRef}
              {...provided.droppableProps}
              spacing={1}
              sx={{
                minHeight: 72,
                borderRadius: 1,
                p: 1,
                bgcolor: snapshot.isDraggingOver ? "action.hover" : "background.default",
                border: "2px dashed",
                borderColor: snapshot.isDraggingOver ? "primary.main" : "divider",
                transition: "background-color 0.15s, border-color 0.15s",
              }}
            >
              {sectionProducts.length === 0 && !snapshot.isDraggingOver && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  textAlign="center"
                  sx={{ py: 2, display: "block" }}
                >
                  Arrastra tus {t('product', 2)} aquí
                </Typography>
              )}

              {sectionProducts.map((product, pidx) => {
                const isProductDisabled = Boolean(product?.isDisabled) || Boolean(activeBranch && product?.branchSettings?.[activeBranch]?.isDisabled);

                return (
                  <DraggableComp
                    key={product.id}
                    draggableId={`section-product-${sectionIndex}-${product.id}`}
                    index={pidx}
                  >
                    {(prov: import("react-beautiful-dnd").DraggableProvided, snap: import("react-beautiful-dnd").DraggableStateSnapshot) => (
                      <Card
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        {...prov.dragHandleProps}
                        variant="outlined"
                        sx={{
                          cursor: "grab",
                          userSelect: "none",
                          opacity: snap.isDragging ? 0.85 : (isProductDisabled ? 0.5 : 1),
                          boxShadow: snap.isDragging ? 3 : 0,
                          bgcolor: isProductDisabled ? "action.hover" : "background.paper",
                        }}
                      >
                        <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {product.images?.[0] ? (
                              <CardMedia
                                component="img"
                                image={getThumbnailImage({
                                  file: product.images,
                                  organizationId: activeOrganization,
                                  size: "150x150",
                                })}
                                sx={{ width: 36, height: 36, borderRadius: 0.75, objectFit: "cover", flexShrink: 0 }}
                              />
                            ) : (
                              <Box sx={{ width: 36, height: 36, borderRadius: 0.75, bgcolor: "action.hover", flexShrink: 0 }} />
                            )}
                            <Typography
                              variant="body2"
                              flex={1}
                              noWrap
                              sx={{
                                textDecoration: isProductDisabled ? "line-through" : "none",
                                color: isProductDisabled ? "text.disabled" : "text.primary",
                              }}
                            >
                              {product.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color="text.secondary"
                              sx={{
                                flexShrink: 0,
                                pl: 1,
                                pr: 1,
                                textDecoration: isProductDisabled ? "line-through" : "none",
                                color: isProductDisabled ? "text.disabled" : "text.secondary",
                              }}
                            >
                              <Price currency="NIO">{product.price != 0 ? product.price : (product.startingPrice ?? 0)}</Price>
                            </Typography>

                            <Tooltip title={isProductDisabled ? `Mostrar ${t('product')}` : `Ocultar ${t('product')}`}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleProductVisibility(product, isProductDisabled);
                                }}
                              >
                                <HugeiconsIcon
                                  icon={isProductDisabled ? ViewOffSlashIcon : ViewIcon}
                                  size={14}
                                  strokeWidth={2}
                                />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Quitar de la sección">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRemoveProduct(product.id);
                                }}
                                sx={{ color: "text.disabled" }}
                              >
                                <HugeiconsIcon icon={Cancel01Icon} size={14} strokeWidth={2} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </CardContent>
                      </Card>
                    )}
                  </DraggableComp>
                );
              })}
              {provided.placeholder as any}
            </Stack>
          )}
        </DroppableComp>
      </AccordionDetails>
    </Accordion>
  );
};

export default SectionCard;
